import XRegExp from 'xregexp';
import * as Yup from 'yup';
import { SUPPORTED_FORMATS } from '../../constants/constants';
import userTypeEnum from '../../constants/enums/user-type-enum';

const orderIdRegex = XRegExp('^[\\p{L}\\p{N}-]+$');

export const UserChangePasswordValidateSchema = Yup.object({
  NewPassword: Yup.string()
    .min(8, 'min_length_required')
    .max(20, 'min_length_required')
    .matches(
      /^(?=.*[A-Z])(?=.*\d)(?=.*[^\da-zA-Z]).{8,}$/,
      'user_validation_password_matches'
    )
    .required('field_required'),
  ConfirmPassword: Yup.string()
    .min(8, 'min_length_required')
    .max(20, 'min_length_required')
    .matches(
      /^(?=.*[A-Z])(?=.*\d)(?=.*[^\da-zA-Z]).{8,}$/,
      'user_validation_password_matches'
    )
    .required('field_required')
    .test('different-password', 'same_password_message', function (value) {
      return value === this.parent.NewPassword;
    }),
});

export const LoginValidateSchema = Yup.object({
  UserId: Yup.string().required('field_required'),
  Password: Yup.string().trim().required('field_required'),
});

export const AddUserValidateSchema = Yup.object({
  UserName: Yup.string()
    .min(1, 'min_user_name_length_required')
    .max(256, 'min_user_name_length_required')
    // .matches(/^[A-Za-z][A-Za-z0-9\s]*$/, 'whitespace_error')
    .required('field_required')
    .test('no-leading-whitespace', 'whitespace_blank_error', (value) => {
      if (typeof value !== 'string') return false;
      return !value.startsWith(' ');
    }),
  Password: Yup.string()
    .min(8, 'min_length_required')
    .max(256, 'min_length_required')
    .matches(
      /^(?=.*[A-Z])(?=.*\d)(?=.*[^\da-zA-Z]).{8,}$/,
      'user_validation_password_matches'
    )
    .required('field_required'),
  ConfirmPassword: Yup.string().test(
    'different-password',
    'confirm_password_match',
    function (value) {
      return value === this.parent.Password;
    }
  ),
  AccountStatus: Yup.number().min(0, 'field_required'),
  Email: Yup.string()
    .matches(
      /^(([^<>()\]\\.,;:\s@"]+(\.[^<>()\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([一-龠ぁ-ゔァ-ヴー々〆〤a-zA-Z\-0-9]+\.)+[一-龠ぁ-ゔァ-ヴー々〆〤a-zA-Z]{2,}))$/,
      'invalid_login_id'
    )
    .min(5, 'lodin_id_length_required')
    .max(256, 'lodin_id_length_required')
    .email()
    .when('AccountStatus', {
      is: 0,
      then: (schema) => schema.required('field_required'),
      otherwise: (schema) => schema.notRequired(),
    }),
  UserType: Yup.number().min(0, 'field_required'),
  TenantId: Yup.number().test(
    'Required-field',
    'field_required',
    function (value) {
      return (
        value > 0 ||
        this.parent.UserType === userTypeEnum.SuperAdministrator ||
        this.parent.UserType === userTypeEnum.DIDManager
      );
    }
  ),
  UserId: Yup.string()
    .required('field_required')
    .matches(/^\S*$/, 'whitespace_error')
    .min(5, 'user_id_length_required')
    .max(256, 'user_id_length_required'),
});

export const EditUserValidateSchema = Yup.object({
  UserName: Yup.string()
    .min(1, 'min_user_name_length_required')
    .max(256, 'min_user_name_length_required')
    // .matches(/^[A-Za-z][A-Za-z0-9\s]*$/, 'whitespace_error')
    .required('field_required')
    .test('no-leading-whitespace', 'whitespace_blank_error', (value) => {
      if (typeof value !== 'string') return false;
      return !value.startsWith(' '); // Must start with a letter (A-Z or a-z)
    }),
  Email: Yup.string()
    .matches(
      /^(([^<>()\]\\.,;:\s@"]+(\.[^<>()\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([一-龠ぁ-ゔァ-ヴー々〆〤a-zA-Z\-0-9]+\.)+[一-龠ぁ-ゔァ-ヴー々〆〤a-zA-Z]{2,}))$/,
      'invalid_login_id'
    )
    .min(1, 'login_id_length_required')
    .max(256, 'login_id_length_required')
    .required('field_required')
    .when('AccountStatus', {
      is: 0,
      then: (schema) => schema.required('field_required'),
      otherwise: (schema) => schema.notRequired(),
    })
    .email(),
  UserType: Yup.number().min(0, 'field_required'),
});

export const FactoryDetailsValidationSchema = Yup.object({
  FactoryName: Yup.string()
    .required('field_required')
    .max(256, 'max_char_limit_reached')
    .test('no-leading-whitespace', 'whitespace_error', (value) => {
      if (typeof value !== 'string') return false;
      return !value.startsWith(' ');
    }),
  FactoryType: Yup.number().min(0, 'field_required'),
  FactoryAddress: Yup.string().max(2048, 'factory_address_char_limit_reached'),
  FactoryCity: Yup.string()
    .max(256, 'max_char_limit_reached')
    .required('field_required')
    .test('no-leading-whitespace', 'whitespace_error', (value) => {
      if (typeof value !== 'string') return false;
      return !value.startsWith(' ');
    }),
  FactoryCountry: Yup.string().max(256, 'max_char_limit_reached'),
});

export const UpdateTenantDetailsValidateSchema = Yup.object({
  FactoryName: Yup.string()
    .required('field_required')
    .max(256, 'max_char_limit_reached')
    .test('no-leading-whitespace', 'whitespace_error', (value) => {
      if (typeof value !== 'string') return false;
      return !value.startsWith(' ');
    }),
  FactoryType: Yup.number().min(0, 'field_required'),
  FactoryAddress: Yup.string().max(2048, 'factory_address_char_limit_reached'),
  FactoryCity: Yup.string().max(256, 'max_char_limit_reached'),
  FactoryCountry: Yup.string().max(256, 'max_char_limit_reached'),
});

export const FactoryUserValidationSchema = Yup.object({
  UserName: Yup.string()
    // .matches(/^\S*$/, 'whitespace_error')
    .min(1, 'min_user_name_length_required')
    .max(25, 'min_user_name_length_required')
    .required('field_required')
    .test('no-leading-whitespace', 'whitespace_blank_error', (value) => {
      if (typeof value !== 'string') return false;
      return !value.startsWith(' ');
    }),
  Password: Yup.string()
    .min(8, 'min_length_required')
    .max(20, 'min_length_required')
    .matches(
      /^(?=.*[A-Z])(?=.*\d)(?=.*[^\da-zA-Z]).{8,}$/,
      'user_validation_password_matches'
    )
    .required('field_required')
    .test('no-leading-whitespace', 'whitespace_error', (value) => {
      if (typeof value !== 'string') return false;
      return !value.startsWith(' ');
    }),
  ConfirmPassword: Yup.string()
    .required('field_required')
    .test('different-password', 'confirm_password_match', function (value) {
      return value === this.parent.Password;
    }),
  Email: Yup.string()
    .matches(
      /^(([^<>()\]\\.,;:\s@"]+(\.[^<>()\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([一-龠ぁ-ゔァ-ヴー々〆〤a-zA-Z\-0-9]+\.)+[一-龠ぁ-ゔァ-ヴー々〆〤a-zA-Z]{2,}))$/,
      'invalid_login_id'
    )
    .min(5, 'lodin_id_length_required')
    .max(50, 'lodin_id_length_required')
    .email()
    .when('AccountStatus', {
      is: 0,
      then: (schema) => schema.required('field_required'),
      otherwise: (schema) => schema.notRequired(),
    }),
  Comment: Yup.string().max(256, 'max_char_limit_reached'),
  UserId: Yup.string()
    .required('field_required')
    .matches(/^\S*$/, 'whitespace_error')
    .min(5, 'user_id_length_required')
    .max(256, 'user_id_length_required'),
  AccountStatus: Yup.number().min(0, 'field_required'),
});

export const UpdatePasswordValidateSchema = Yup.object({
  OldPassword: Yup.string().required('field_required'),
  NewPassword: Yup.string()
    .min(8, 'min_length_required')
    .max(20, 'min_length_required')
    .matches(
      /^(?=.*[A-Z])(?=.*\d)(?=.*[^\da-zA-Z]).{8,}$/,
      'user_validation_password_matches'
    )
    .required('field_required')
    .test('different-password', 'different_password_message', function (value) {
      return value !== this.parent.OldPassword;
    }),
});
// .required("")

export const ProcessValidateSchema = Yup.object({
  ProcessName: Yup.string()
    .required('field_required')
    .max(256, 'max_process_limit_reached')
    .test('no-leading-whitespace', 'whitespace_error', (value) => {
      if (typeof value !== 'string') return false;
      return !value.startsWith(' ');
    }),
  Type: Yup.number().min(0, 'field_required'),
  Comment: Yup.string().max(2048, 'max_process_comment_limit_reached'),
  ShortName: Yup.string()
    .required('field_required')
    .max(5, 'max_char_limit_5')
    .test('no-leading-whitespace', 'whitespace_error', (value) => {
      if (typeof value !== 'string') return false;
      return !value.startsWith(' ');
    }),
});

export const TeplarValidateSchema = Yup.object({
  formFile: Yup.mixed<File>()
    .required('file_required')
    .test(
      'CSVfileFormat',
      'invalid_file_format',
      (value: File | null | undefined) => {
        return value ? SUPPORTED_FORMATS.includes(value.type) : false;
      }
    ),
});

export const UpdateTeplarValidateSchema = Yup.object({
  ProductId: Yup.string()
    .required('field_required')
    .test('no-leading-whitespace', 'whitespace_error', (value) => {
      if (typeof value !== 'string') return false;
      return !value.startsWith(' ');
    }),
  Width: Yup.string()
    .test('no-leading-chars', 'only_numbers_allowed', (value) => {
      if (value === undefined || value === null || value === '') return true; // allow empty
      if (typeof value !== 'string') return false;
      return !value.startsWith(' ');
    })
    .matches(/^\d*$/, 'only_numbers_allowed'), // allow empty or digits only

  Layout: Yup.string()
    .test('no-leading-chars', 'only_numbers_allowed', (value) => {
      if (value === undefined || value === null || value === '') return true; // allow empty
      if (typeof value !== 'string') return false;
      return !value.startsWith(' ');
    })
    .matches(/^\d*$/, 'only_numbers_allowed'),
});

export const FilterValidationSchema = Yup.object().shape({});

export const DashboardFilterValidationSchema = Yup.object().shape({});

export const AIMSPortalValidateSchema = Yup.object({
  NearDeadlineTemplateId: Yup.number().min(0, 'field_required'),
  DeadlineCrossTemplateId: Yup.number().min(0, 'field_required'),
  RegularModeTemplateId: Yup.number().min(0, 'field_required'),
  NearDeadLineDate: Yup.string()
    .matches(/^\d+$/, 'only_numbers_are_allowed')
    .test('max-99', 'must_be_less_than_or_equal_to_99', (value) => {
      if (!value) return true;
      const num = parseInt(value, 10);
      return !isNaN(num) && num <= 99;
    }),
  // PatternForMissingPartTemplateId: Yup.number().min(0, 'field_required'),
  StationCode: Yup.string().required('field_required'),
  Url: Yup.string().required('field_required'),
});

export const ProcessTemplateValidateSchema = Yup.object({
  TemplateName: Yup.string()
    .required('field_required')
    .test('no-leading-whitespace', 'whitespace_error', (value) => {
      if (typeof value !== 'string') return false;
      return !value.startsWith(' ');
    })
    .max(256, 'max_char_limit_reached')
    .min(5, 'min_char_limit_5'),
  Remarks: Yup.string().max(1024, 'max_char_limit_reached_1024'),
  SelectedProcesses: Yup.number().test(
    'atleast_a_process',
    'atleast_a_process',
    function (value) {
      return value > 0;
    }
  ),
});

export const HolidayCalendarValidationSchema = Yup.object().shape({
  HolidayDate: Yup.date().required('field_required').nullable(),
  Comment: Yup.string(),
});

export const ProductValidationSchema = Yup.object().shape({
  ProductName: Yup.string()
    .required('field_required')
    .max(10, 'max_char_product_name')
    .matches(/^[0-9]+$/, 'only_numbers_allowed')
    .test('no-leading-whitespace', 'whitespace_error', (value) => {
      if (typeof value !== 'string') return false;
      return !value.startsWith(' ');
    }),
  Katashiki: Yup.string()
    .max(256, 'max_char_limit_256')
    .test('no-leading-whitespace', 'whitespace_code_error', (value) => {
      if (typeof value !== 'string') return true;
      return !value.startsWith(' ');
    }),
  Customer: Yup.string().max(256, 'max_char_limit_256'),
  TraySize: Yup.string().max(256, 'max_char_limit_256'),
  KeyInfo1: Yup.string().max(256, 'max_char_limit_256'),
  KeyInfo2: Yup.string().max(256, 'max_char_limit_256'),
  KeyInfo3: Yup.string().max(256, 'max_char_limit_256'),
  Remarks: Yup.string().max(500, 'max_char_limit_500'),
});

export const TrayValidationSchema = Yup.object({
  TrayId: Yup.string()
    .required('field_required')
    .max(256, 'max_char_limit_256')
    .test('no-leading-whitespace', 'whitespace_error', (value) => {
      if (typeof value !== 'string') return false;
      return !value.startsWith(' ');
    })
    .matches(/^[0-9-]+$/, 'only_numbers_and_hyphen_allowed'),
  ProductId: Yup.string()
    .transform((value) => (value ? value.trim() : value))
    .max(10, 'max_char_product_name')
    .test('product-required', 'Please select a product', function (value) {
      return value && String(value) !== '-1';
    })
    .test(
      'product-must-be-number',
      'Product ID must be a number',
      function (value) {
        if (!value || String(value) === '-1') return true; // Skip if missing
        return !isNaN(Number(value));
      }
    )
    .nullable(),
  OrderId: Yup.string()
    .max(256, 'max_char_limit_256')
    .test(
      'valid-orderid',
      'only_alphanumeric_and_hyphen_allowed',
      (value) => {
        if (!value) return true;
        return XRegExp.test(value, orderIdRegex);
      }
    ),
  Katashiki1: Yup.string().max(1024, 'max_char_limit_1024'),
  Katashiki2: Yup.string().max(1024, 'max_char_limit_1024'),
  Kishu: Yup.string().max(1024, 'max_char_limit_1024'),
  EndUserOrderId: Yup.string().max(256, 'max_char_limit_256'),
  EndUserName: Yup.string().max(256, 'max_char_limit_256'),
  MissingParts: Yup.string().max(1024, 'max_char_limit_1024'),
  Group: Yup.string().max(128, 'max_char_limit_125'),
  CurrentState: Yup.string().max(256, 'max_char_limit_256'),
  Remarks: Yup.string().max(1024, 'max_char_limit_1024'),
  CustomerDueDate: Yup.string().required('field_required'),
  InvoiceNumber: Yup.string().matches(/^\d*$/, 'only_numbers_allowed'),
  SubContractorName: Yup.string().max(256, 'max_char_limit_256'),
  ProcessName: Yup.string().max(256, 'max_char_limit_256'),
  QuantityUnitName: Yup.string().max(50, 'max_char_limit_50'),
  ProductComment1: Yup.string().max(256, 'max_char_limit_256'),
  ProductComment2: Yup.string().max(256, 'max_char_limit_256'),
  ProductComment3: Yup.string().max(256, 'max_char_limit_256'),
  ProductComment4: Yup.string().max(256, 'max_char_limit_256'),
  ProductComment5: Yup.string().max(256, 'max_char_limit_256'),
  ProductComment6: Yup.string().max(256, 'max_char_limit_256'),
  CustomerId: Yup.string().max(256, 'max_char_limit_256'),
});

export const TrayFilterValidationSchema = Yup.object({
  TrayId: Yup.string()
    .max(256, 'max_char_limit_256')
    // .test('no-leading-whitespace', 'whitespace_error', (value) => {
    //   if (typeof value !== 'string') return false;
    //   return !value.startsWith(' ');
    // })
    .matches(/^[0-9-]+$/, 'only_numbers_and_hyphen_allowed'),
  ProductId: Yup.string()
    .transform((value) => (value ? value.trim() : value))
    .max(10, 'max_char_product_name')
    // .test('product-required', 'please_select_a_product', function (value) {
    //   return value && String(value) !== '-1';
    // })
    .test(
      'product-must-be-number',
      'Product ID must be a number',
      function (value) {
        if (!value || String(value) === '-1') return true; // Skip if missing
        return !isNaN(Number(value));
      }
    )
    .nullable(),
  OrderId: Yup.string()
    .max(256, 'max_char_limit_256')
    .test(
      'valid-orderid',
      'only_alphanumeric_and_hyphen_allowed',
      (value) => {
        if (!value) return true;
        return XRegExp.test(value, orderIdRegex);
      }
    ),
  EndUserOrderId: Yup.string().max(256, 'max_char_limit_256'),
  CustomerDueDate: Yup.string(),
});

export const OrderTableFilterValidationSchema = Yup.object({
  TrayId: Yup.string()
    .max(256, 'max_char_limit_256')
    .matches(/^[0-9-]+$/, 'only_numbers_and_hyphen_allowed'),
  ProductId: Yup.string()
    .transform((value) => (value ? value.trim() : value))
    .max(10, 'max_char_product_name')
    .test(
      'product-must-be-number',
      'Product ID must be a number',
      function (value) {
        if (!value || String(value) === '-1') return true; // Skip if missing
        return !isNaN(Number(value));
      }
    )
    .nullable(),
  OrderId: Yup.string()
    .max(256, 'max_char_limit_256')
    .test(
      'valid-orderid',
      'only_alphanumeric_and_hyphen_allowed',
      (value) => {
        if (!value) return true;
        return XRegExp.test(value, orderIdRegex);
      }
    ),
  EndUserOrderId: Yup.string().max(256, 'max_char_limit_256'),
  CustomerDueDate: Yup.string(),
  Katashiki1: Yup.string().max(1024, 'max_char_limit_1024'),
  Katashiki2: Yup.string().max(1024, 'max_char_limit_1024'),
  Kishu: Yup.string().max(1024, 'max_char_limit_1024'),
  MaterialProductId: Yup.string().max(1024, 'max_char_limit_1024'),
  MaterialKatashiki: Yup.string().max(1024, 'max_char_limit_1024'),
});

export const GroupValidateSchema = Yup.object({
  GroupName: Yup.string()
    .required('field_required')
    .max(4, 'max_group_char_limit_reached')
    .test('no-leading-whitespace', 'whitespace_code_error', (value) => {
      if (typeof value !== 'string') return false;
      return !value.startsWith(' ');
    }),
});

export const CodeValidateSchema = Yup.object({
  CodeName: Yup.string()
    // .required('field_required')
    .min(2, 'min_group_code_limit_reached')
    .max(250, 'max_process_limit_reached')
  // .test('no-leading-whitespace', 'whitespace_code_error', (value) => {
  //   if (typeof value !== 'string') return false;
  //   return !value.startsWith(' ');
  // }),
});

export const WeekendCalendarValidationSchema = Yup.object().shape({
  Weekend_1: Yup.string().required('Weekend 1 is required'),
  Weekend_2: Yup.string()
    .required('Weekend 2 is required')
    .test('not-same-as-weekend-1', 'same_weekend_error', function (value) {
      const weekend1 = this.parent.Weekend_1;

      // Allow both -1
      if (weekend1 === '-1' && value === '-1') {
        return true;
      }

      return value !== weekend1;
    }),
});

export const KishuValidateSchema = Yup.object({
  Kishu: Yup.string()
    .required('field_required')
    .test('no-leading-whitespace', 'whitespace_blank_error', (value) => {
      if (typeof value !== 'string') return false;
      return !value.startsWith(' ');
    })
    .max(1024, 'max_char_limit_reached_1024')
    .min(1, 'min_char_limit'),
  KishuMark: Yup.string().test('please_select', 'please_select', (value) => {
    return value !== 'please_select';
  }),
  // KishuMark: Yup.number().min(0, 'field_required'),
  Remarks: Yup.string().max(1024, 'max_char_limit_reached_1024'),
});

export const CopyProductValidationSchema = Yup.object({
  NewProductId: Yup.string()
    .required('field_required')
    .max(10, 'max_char_product_name')
    .matches(/^[0-9]+$/, 'only_numbers_allowed')
    .test('no-leading-whitespace', 'whitespace_code_error', (value) => {
      if (typeof value !== 'string') return false;
      return !value.startsWith(' ');
    }),
});

export const RemoveTaskValidationSchema = Yup.object({
  AbnormalityRemark: Yup.string()
    .required('field_required')
    .max(1024, 'max_char_limit_reached'),
});

export const LEDBlinkValidationSchema = Yup.object({
  ColorCode: Yup.number().min(0, 'field_required'),
  Second: Yup.string().required(),
});

export const DimensionDataValidateSchema = Yup.object({
  Messages: Yup.string()
    .required('field_required')
    .max(256, 'max_char_limit_reached')
    .test('no-leading-whitespace', 'whitespace_blank_error', (value) => {
      if (typeof value !== 'string') return false;
      return !value.startsWith(' ');
    }),
});

export const CheckItemTemplateValidateSchema = Yup.object({
  CheckItemName: Yup.string()
    .required('field_required')
    .test('no-leading-whitespace', 'whitespace_error', (value) => {
      if (typeof value !== 'string') return false;
      return !value.startsWith(' ');
    })
    .max(256, 'max_char_limit_reached')
    .min(1, 'min_char_limit_1'),
  ProcessId: Yup.number().test('atleast_a_process', 'atleast_a_process', (value) => {
    return value && value > 0;
  }),
  Remarks: Yup.string().max(1024, 'max_char_limit_reached_1024'),
});

/* Filter validation schemas */
export const TeplarPrintFilter = Yup.object({
  ProductId: Yup.string()
    .max(10, 'max_char_product_name')
    .test('no-leading-chars', 'only_numbers_allowed', (value) => {
      if (value === undefined || value === null || value === '') return true; // allow empty
      if (typeof value !== 'string') return false;
      return !value.startsWith(' ');
    })
    .matches(/^\d*$/, 'only_numbers_allowed'), // allow empty or digits only
});
